import { useEffect, useMemo, useState, type JSX } from 'react';
import { FolderOpen, Inbox, Info, Search, SlidersHorizontal, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/ui/combobox';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterField } from '@/components/ui/filter-field';
import { PageHeader } from '@/components/ui/page-header';
import { useMe } from '@/hooks/useMe';
import { useSeenFolders } from '@/hooks/useSeenFolders';
import { isRecentlyReleased } from '@/lib/newFolders';
import { useUrlState } from '@/hooks/useUrlState';
import {
  NO_FOLDER_KEY,
  QUEUE_LEGEND,
  QUEUE_TABS,
  enrichmentField,
  folderKey,
  handleKey,
  hasBriefing,
  queueTabFor,
  resolveQueueTab,
  useAssignedCounts,
  useDiscoveryFolders,
  useMyMessagedTimes,
  useQueueLeads,
  type QueueTab,
} from '@/hooks/useDiscoveryQueue';
import { ProspectLeadRow } from '@/components/discovery-queue/ProspectLeadRow';
import { LogReplyDialog } from '@/components/discovery-queue/dialogs';
import { AllBriefingsStrip, FolderBriefingStrip } from '@/components/discovery-queue/BriefingStrip';
import { cn } from '@/lib/utils';
import type { DiscoveryFolderSummary, DiscoveryProspect } from '@/api/types';

/**
 * Outreacher's Lead queue: one worklist of every lead sent to them or their
 * team, across folders.
 *
 *   - Three tabs follow the flow: To message → Messaged · waiting → Done.
 *   - Folders are filter chips (with counts), not a grid to click through.
 *     A folder released in the last few days that you haven't opened yet
 *     gets a NEW badge and an outline, and is never hidden under "+N more".
 *   - Each row has one primary action for its tab; the rest sit in a kebab.
 *   - The folder's briefing is a collapsible strip, open the first time.
 *
 * Log reply creates a real outreach_invite attributed to the caller. No claim
 * flow: anyone on the team can work anything; the unique (tenant, channel,
 * target) constraint is the safety net for double-logging.
 */

/** How many rows to put in the DOM at once. Filtering and counting run over
 *  everything loaded regardless; this only bounds what is painted. */
const RENDER_CHUNK = 150;
/** Chips shown before "+N more". */
const CHIP_PREVIEW = 10;

type SortKey = 'followers' | 'avgViews' | 'newest' | 'waiting';
type WhoFilter = 'any' | 'nobody' | 'teammate';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'followers', label: 'Most followers' },
  { value: 'avgViews', label: 'Most avg views' },
  { value: 'newest', label: 'Newest first' },
];
const WAITING_SORT = { value: 'waiting' as SortKey, label: 'Waiting longest' };

const WHO_OPTIONS: { value: WhoFilter; label: string }[] = [
  { value: 'nobody', label: 'Nobody messaged yet' },
  { value: 'teammate', label: 'A teammate messaged' },
];
const FOLLOWER_STEPS = [
  { value: '10000', label: '10K+' },
  { value: '50000', label: '50K+' },
  { value: '100000', label: '100K+' },
  { value: '500000', label: '500K+' },
];
const VIEW_STEPS = [
  { value: '10000', label: '10K+' },
  { value: '50000', label: '50K+' },
  { value: '100000', label: '100K+' },
];

const URL_DEFAULTS = {
  tab: 'message' as string,
  folder: '' as string,
  q: '' as string,
  sort: 'followers' as string,
  who: 'any' as string,
  minf: '' as string,
  minv: '' as string,
  mine: false as boolean,
};

function numericField(p: DiscoveryProspect, key: 'followers' | 'avgViews'): number {
  const v = enrichmentField(p, key);
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

export function DiscoveryQueuePage(): JSX.Element {
  const [url, setUrl] = useUrlState(URL_DEFAULTS);
  const tab = resolveQueueTab(url.tab);

  const me = useMe();
  const myInviterId = me.data?.role === 'outreacher' ? me.data.inviter.id : null;
  const myTeam = me.data?.role === 'outreacher' ? me.data.inviter.team : null;

  // Open + messaged leads first; logged ones (for Done) once those are in, so
  // the worklist itself is never waiting on history.
  const main = useQueueLeads();
  const mainReady = !main.isLoading && !main.stillLoading;
  const logged = useQueueLeads('PROMOTED', mainReady);
  const loggedReady = mainReady && !logged.isLoading && !logged.stillLoading;
  const messagedTimes = useMyMessagedTimes();
  const foldersQuery = useDiscoveryFolders();
  const assigned = useAssignedCounts();

  const [activeProspect, setActiveProspect] = useState<DiscoveryProspect | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showAllChips, setShowAllChips] = useState(false);

  // Every lead with its tab. A lead logged between the two fetches can show
  // up in both lists; the logged copy wins.
  const tabbed = useMemo(() => {
    const byId = new Map<string, DiscoveryProspect>();
    for (const p of main.leads) byId.set(p.publicId, p);
    for (const p of logged.leads) byId.set(p.publicId, p);
    const out: { p: DiscoveryProspect; tab: QueueTab }[] = [];
    for (const p of byId.values()) {
      const t = queueTabFor(p);
      if (t) out.push({ p, tab: t });
    }
    return out;
  }, [main.leads, logged.leads]);

  const folderSummaries = useMemo(() => {
    const m = new Map<string, DiscoveryFolderSummary>();
    for (const f of foldersQuery.data ?? []) m.set(folderKey(f.sourceFolderPublicId), f);
    return m;
  }, [foldersQuery.data]);

  // Folder chips: every folder that still has workable leads (to message or
  // waiting on), most recently released first so new briefs lead and old
  // folders sink under "+N more". The count is for the tab you're on.
  const chips = useMemo(() => {
    const byFolder = new Map<string, { key: string; id: string | null; name: string; workable: number; inTab: number }>();
    for (const { p, tab: t } of tabbed) {
      const key = folderKey(p.sourceFolderPublicId);
      const entry = byFolder.get(key) ?? {
        key,
        id: p.sourceFolderPublicId,
        name: p.sourceFolderName ?? folderSummaries.get(key)?.sourceFolderName ?? 'Unfiled',
        workable: 0,
        inTab: 0,
      };
      if (t !== 'done') entry.workable += 1;
      if (t === tab) entry.inTab += 1;
      byFolder.set(key, entry);
    }
    return [...byFolder.values()]
      .filter((c) => c.workable > 0 || c.key === url.folder)
      .sort((a, b) => {
        const ra = folderSummaries.get(a.key)?.lastExportedAt ?? '';
        const rb = folderSummaries.get(b.key)?.lastExportedAt ?? '';
        return rb.localeCompare(ra) || b.workable - a.workable || a.name.localeCompare(b.name);
      });
  }, [tabbed, tab, url.folder, folderSummaries]);

  // NEW chips. Opening a folder any way (chip, briefing, link) counts as seen.
  const seenFolders = useSeenFolders(myInviterId);
  const { markSeen } = seenFolders;
  useEffect(() => {
    if (url.folder && url.folder !== NO_FOLDER_KEY) markSeen(url.folder);
  }, [url.folder, markSeen]);
  const newKeys = useMemo(() => {
    const out = new Set<string>();
    if (!seenFolders.ready) return out;
    const now = Date.now();
    for (const c of chips) {
      if (c.key === NO_FOLDER_KEY || c.key === url.folder || seenFolders.seen.has(c.key)) continue;
      if (isRecentlyReleased(folderSummaries.get(c.key)?.lastExportedAt, now)) out.add(c.key);
    }
    return out;
  }, [chips, folderSummaries, seenFolders.ready, seenFolders.seen, url.folder]);

  const inFolder = useMemo(
    () => (url.folder ? tabbed.filter(({ p }) => folderKey(p.sourceFolderPublicId) === url.folder) : tabbed),
    [tabbed, url.folder],
  );
  const tabCounts = useMemo(() => {
    const c: Record<QueueTab, number> = { message: 0, waiting: 0, done: 0 };
    for (const { tab: t } of inFolder) c[t] += 1;
    return c;
  }, [inFolder]);

  const sort: SortKey =
    url.sort === 'waiting' ? (tab === 'waiting' ? 'waiting' : 'followers')
    : (SORT_OPTIONS.some((o) => o.value === url.sort) ? (url.sort as SortKey) : 'followers');
  const who = (['any', 'nobody', 'teammate'] as const).includes(url.who as WhoFilter) ? (url.who as WhoFilter) : 'any';
  const minFollowers = url.minf ? Number(url.minf) : null;
  const minAvgViews = url.minv ? Number(url.minv) : null;
  const hiddenFilterCount =
    (who !== 'any' ? 1 : 0) + (minFollowers !== null ? 1 : 0) + (minAvgViews !== null ? 1 : 0) + (url.mine ? 1 : 0);
  const filtered = url.q.trim() !== '' || hiddenFilterCount > 0;

  const tabRows = useMemo(() => inFolder.filter((x) => x.tab === tab).map((x) => x.p), [inFolder, tab]);
  const rows = useMemo(() => {
    const q = url.q.trim().toLowerCase().replace(/^@/, '');
    const out = tabRows.filter((p) => {
      if (q) {
        const nick = String(enrichmentField(p, 'nickname') ?? '').toLowerCase();
        if (!p.targetIdentifier.toLowerCase().includes(q) && !nick.includes(q)) return false;
      }
      const others = (p.contactedBy ?? []).filter((c) => c.inviterId !== myInviterId);
      if (who === 'nobody' && (p.contactedBy ?? []).length > 0) return false;
      if (who === 'teammate' && others.length === 0) return false;
      if (url.mine) {
        const toMe = myInviterId != null && p.assignedToInviterId === myInviterId;
        const toTeam = myTeam != null && p.assignedToTeam === myTeam;
        if (!toMe && !toTeam) return false;
      }
      if (minFollowers !== null && numericField(p, 'followers') < minFollowers) return false;
      if (minAvgViews !== null && numericField(p, 'avgViews') < minAvgViews) return false;
      return true;
    });
    const lastMessaged = (p: DiscoveryProspect): number => {
      const t = messagedTimes.get(handleKey(p.channel, p.targetIdentifier))?.lastContactedAt;
      return t ? new Date(t).getTime() : Number.POSITIVE_INFINITY;
    };
    // Leads handed to me come first, then my team's, then the shared pool;
    // the chosen sort orders within each group.
    const priority = (p: DiscoveryProspect): number =>
      myInviterId != null && p.assignedToInviterId === myInviterId
        ? 0
        : myTeam != null && p.assignedToTeam === myTeam
          ? 1
          : 2;
    return out.sort((a, b) => {
      const byAssignment = priority(a) - priority(b);
      if (byAssignment !== 0) return byAssignment;
      if (sort === 'newest') return b.createdAt.localeCompare(a.createdAt);
      if (sort === 'waiting') return lastMessaged(a) - lastMessaged(b);
      const key = sort === 'followers' ? 'followers' : 'avgViews';
      return numericField(b, key) - numericField(a, key);
    });
  }, [tabRows, url.q, url.mine, who, minFollowers, minAvgViews, sort, myInviterId, myTeam, messagedTimes]);

  const [renderLimit, setRenderLimit] = useState(RENDER_CHUNK);
  const resetKey = `${tab}|${url.folder}|${url.q}|${who}|${url.minf}|${url.minv}|${url.mine}|${sort}`;
  useEffect(() => setRenderLimit(RENDER_CHUNK), [resetKey]);
  const visible = rows.slice(0, renderLimit);
  const hiddenByRenderCap = rows.length - visible.length;

  const selectedSummary = url.folder ? folderSummaries.get(url.folder) : undefined;
  const briefingFolders = useMemo(
    () =>
      chips
        .filter((c) => c.workable > 0)
        .map((c) => folderSummaries.get(c.key))
        .filter((f): f is DiscoveryFolderSummary => hasBriefing(f)),
    [chips, folderSummaries],
  );

  const clearFilters = (): void =>
    setUrl({ q: '', who: 'any', minf: '', minv: '', mine: false });

  const directCount = assigned.data?.direct ?? 0;
  const teamCount = assigned.data?.team ?? 0;
  const nothingAtAll = !main.isLoading && !main.isError && !main.stillLoading && tabbed.length === 0;
  // The selected chip and NEW ones always show, even past the preview.
  const shownChips =
    showAllChips || chips.length <= CHIP_PREVIEW
      ? chips
      : chips.filter((c, i) => i < CHIP_PREVIEW || c.key === url.folder || newKeys.has(c.key));
  const hiddenChips = chips.length - shownChips.length;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Lead queue"
        description="Every creator an admin sent to you or your team, across all folders."
      >
        {(directCount > 0 || teamCount > 0) && (
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Users className="h-3.5 w-3.5" />
            {directCount > 0 && (
              <span>
                <span className="font-semibold text-foreground">{directCount}</span> assigned to you
              </span>
            )}
            {directCount > 0 && teamCount > 0 && <span aria-hidden>·</span>}
            {teamCount > 0 && (
              <span>
                <span className="font-semibold text-foreground">{teamCount}</span> to your team
              </span>
            )}
          </p>
        )}
      </PageHeader>

      {main.isError ? (
        <p role="alert" className="py-8 text-center text-sm text-destructive">
          Couldn't load your leads: {(main.error as Error | null)?.message ?? 'try again in a moment.'}
        </p>
      ) : nothingAtAll ? (
        <EmptyState
          icon={<Inbox className="h-6 w-6" />}
          title="No leads for you right now."
          hint="New ones appear when an admin sends a folder to you or your team."
        />
      ) : (
        <>
          {/* Folder chips */}
          {chips.length > 0 && (
            <div role="group" aria-label="Folder" className="flex flex-wrap items-center gap-x-1.5 gap-y-2.5">
              <FolderChip
                label="All folders"
                count={tabbed.filter((x) => x.tab === tab).length}
                active={url.folder === ''}
                onClick={() => setUrl({ folder: '' })}
              />
              {shownChips.map((c) => (
                <FolderChip
                  key={c.key}
                  label={c.name}
                  count={c.inTab}
                  active={url.folder === c.key}
                  isNew={newKeys.has(c.key)}
                  onClick={() => setUrl({ folder: url.folder === c.key ? '' : c.key })}
                />
              ))}
              {hiddenChips > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setShowAllChips(true)}>
                  +{hiddenChips} more
                </Button>
              )}
              {showAllChips && chips.length > CHIP_PREVIEW && (
                <Button variant="ghost" size="sm" onClick={() => setShowAllChips(false)}>
                  Fewer
                </Button>
              )}
            </div>
          )}

          {/* Briefing */}
          {url.folder && selectedSummary && hasBriefing(selectedSummary) ? (
            <FolderBriefingStrip key={url.folder} folder={selectedSummary} />
          ) : !url.folder ? (
            <AllBriefingsStrip
              folders={briefingFolders}
              newKeys={newKeys}
              onOpenFolder={(id) => setUrl({ folder: id ?? NO_FOLDER_KEY })}
            />
          ) : null}

          {/* Tabs */}
          <div role="tablist" aria-label="Stage" className="flex flex-wrap gap-1 border-b border-border">
            {QUEUE_TABS.map((t) => {
              const count = t.key === 'done' && !loggedReady ? null : tabCounts[t.key];
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  id={`queue-tab-${t.key}`}
                  aria-selected={tab === t.key}
                  aria-controls="queue-panel"
                  onClick={() => setUrl({ tab: t.key })}
                  className={cn(
                    '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors duration-[120ms]',
                    tab === t.key
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t.label}
                  {count !== null && (
                    <span className="rounded-full bg-muted px-1.5 py-0.5 font-mono text-[11px] leading-none text-muted-foreground">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div role="tabpanel" id="queue-panel" aria-labelledby={`queue-tab-${tab}`} className="space-y-3">
            {/* Search + sort stay visible; everything else is behind Filters. */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative w-full sm:w-64">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  className="pl-8"
                  aria-label="Search handle or name"
                  placeholder="Search handle or name"
                  value={url.q}
                  onChange={(e) => setUrl({ q: e.target.value })}
                />
              </div>
              <Combobox
                className="w-full sm:w-44"
                value={sort}
                onChange={(v) => setUrl({ sort: v || 'followers' })}
                options={tab === 'waiting' ? [...SORT_OPTIONS, WAITING_SORT] : SORT_OPTIONS}
                placeholder="Sort"
              />
              <Button
                variant="outline"
                aria-expanded={filtersOpen}
                aria-controls="queue-filters"
                onClick={() => setFiltersOpen((o) => !o)}
              >
                <SlidersHorizontal className="mr-1 h-3.5 w-3.5" />
                Filters
                {hiddenFilterCount > 0 && (
                  <span className="ml-1 rounded-full bg-brand/15 px-1.5 py-0.5 font-mono text-[11px] leading-none text-brand">
                    {hiddenFilterCount}
                  </span>
                )}
              </Button>
              {filtered && (
                <Button variant="ghost" onClick={clearFilters}>
                  Clear
                </Button>
              )}
            </div>

            {filtersOpen && (
              <div id="queue-filters" className="panel flex flex-wrap items-end gap-3 rounded-xl p-3">
                <FilterField label="Who messaged">
                  <Combobox
                    className="w-full md:w-48"
                    value={who === 'any' ? '' : who}
                    onChange={(v) => setUrl({ who: v || 'any' })}
                    options={WHO_OPTIONS}
                    allowAll
                    allLabel="Anyone or nobody"
                    placeholder="Anyone or nobody"
                  />
                </FilterField>
                <FilterField label="Followers">
                  <Combobox
                    className="w-full md:w-36"
                    value={url.minf}
                    onChange={(v) => setUrl({ minf: v })}
                    options={FOLLOWER_STEPS}
                    allowAll
                    allLabel="Any followers"
                    placeholder="Any followers"
                  />
                </FilterField>
                <FilterField label="Avg views">
                  <Combobox
                    className="w-full md:w-36"
                    value={url.minv}
                    onChange={(v) => setUrl({ minv: v })}
                    options={VIEW_STEPS}
                    allowAll
                    allLabel="Any avg views"
                    placeholder="Any avg views"
                  />
                </FilterField>
                <Button
                  variant={url.mine ? 'default' : 'outline'}
                  aria-pressed={url.mine}
                  onClick={() => setUrl({ mine: !url.mine })}
                >
                  <Users className="mr-1 h-3.5 w-3.5" />
                  Assigned to me or my team
                </Button>
              </div>
            )}

            {tab !== 'done' && (
              <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                <Info className="mt-px h-3.5 w-3.5 shrink-0" />
                {QUEUE_LEGEND}
              </p>
            )}

            {(main.stillLoading || (tab === 'done' && !loggedReady)) &&
              !main.isLoading && (
                <p className="text-xs text-muted-foreground">
                  {main.stillLoading
                    ? `Loading the rest of your leads… ${main.leads.length} of ${main.total}`
                    : 'Loading logged leads…'}
                </p>
              )}

            {filtered && tabRows.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Showing <span className="font-medium text-foreground">{rows.length}</span> of {tabRows.length}
              </p>
            )}

            {main.isLoading || (tab === 'done' && !loggedReady && rows.length === 0) ? (
              <p className="py-10 text-center text-sm text-muted-foreground">Loading your leads…</p>
            ) : rows.length === 0 ? (
              filtered && tabRows.length > 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  <p>No leads match these filters.</p>
                  <Button variant="ghost" size="sm" className="mt-2" onClick={clearFilters}>
                    Clear filters
                  </Button>
                </div>
              ) : (
                <EmptyState
                  icon={<FolderOpen className="h-6 w-6" />}
                  title={EMPTY_TITLE[tab]}
                  hint={EMPTY_HINT[tab]}
                />
              )
            ) : (
              <ul className="panel divide-y divide-border overflow-hidden rounded-xl">
                {visible.map((p) => (
                  <ProspectLeadRow
                    key={p.publicId}
                    prospect={p}
                    tab={tab}
                    myInviterId={myInviterId}
                    myTeam={myTeam}
                    messaged={messagedTimes.get(handleKey(p.channel, p.targetIdentifier))}
                    showFolder={url.folder === ''}
                    onLogReply={setActiveProspect}
                  />
                ))}
              </ul>
            )}

            {hiddenByRenderCap > 0 && (
              <Button variant="outline" onClick={() => setRenderLimit((n) => n + RENDER_CHUNK)}>
                Show {Math.min(hiddenByRenderCap, RENDER_CHUNK)} more
                <span className="ml-1 text-muted-foreground">({hiddenByRenderCap} left)</span>
              </Button>
            )}
          </div>
        </>
      )}

      {activeProspect && (
        <LogReplyDialog prospect={activeProspect} onClose={() => setActiveProspect(null)} />
      )}
    </div>
  );
}

const EMPTY_TITLE: Record<QueueTab, string> = {
  message: 'Nothing left to message here.',
  waiting: 'Nobody you messaged is waiting on a reply.',
  done: 'Nothing done yet.',
};
const EMPTY_HINT: Record<QueueTab, string> = {
  message: 'Check Messaged · waiting for replies to log, or pick another folder.',
  waiting: 'Tap "I messaged them" on a lead after you DM them and it moves here.',
  done: 'Leads move here once you log their reply or skip them as a wrong fit.',
};

function FolderChip({
  label,
  count,
  active,
  isNew = false,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  /** Released recently and not opened yet: warning outline plus a corner tag. */
  isNew?: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'relative inline-flex max-w-[16rem] items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors duration-[120ms]',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input bg-transparent text-foreground hover:bg-accent',
        isNew && 'border-warning ring-2 ring-warning/60',
      )}
    >
      {isNew && (
        <span
          aria-hidden
          className="pointer-events-none absolute -right-1.5 -top-2 rounded-full bg-warning px-1.5 py-0.5 text-[9px] font-bold uppercase leading-none tracking-wider text-warning-foreground shadow-sm"
        >
          New
        </span>
      )}
      {isNew && <span className="sr-only">New folder: </span>}
      <span className="truncate">{label}</span>
      <span
        className={cn(
          'font-mono text-[11px]',
          active ? 'text-primary-foreground/80' : 'text-muted-foreground',
        )}
      >
        {count}
      </span>
    </button>
  );
}
